import { useConfig } from '../hooks';
import { useAssetUrl } from '../hooks/useAsset';

type LogoUsage = 'hero' | 'header';

const VARIANTS: Record<
  LogoUsage,
  {
    default: number;
    responsive: Array<{ maxWidth: number; variant: number }>;
  }
> = {
  hero: {
    default: 1366,
    responsive: [
      { maxWidth: 1024, variant: 768 },
      { maxWidth: 640, variant: 384 },
    ],
  },
  header: {
    default: 384,
    responsive: [{ maxWidth: 640, variant: 192 }],
  },
};

interface VendorLogoProps {
  alt?: string;
  className?: string;
  usage?: LogoUsage;
}

const VendorLogo = ({ alt = 'Logo', className = 'h-24', usage = 'hero' }: VendorLogoProps) => {
  const { data: chatbotConfig } = useConfig();
  const getAssetUrl = useAssetUrl();
  const variantConfig = VARIANTS[usage];
  const defaultImageUrl = getAssetUrl(
    chatbotConfig?.logoId,
    chatbotConfig?.logoCrop,
    variantConfig.default
  );
  const responsiveUrls = variantConfig.responsive.map(({ variant }) => ({
    url: getAssetUrl(chatbotConfig?.logoId, chatbotConfig?.logoCrop, variant),
    variant,
  }));

  if (!defaultImageUrl) {
    return null;
  }

  // Build srcset from the variant configuration
  const srcSetEntries = [
    ...responsiveUrls.map(({ url, variant }) => `${url} ${variant}w`),
    `${defaultImageUrl} ${variantConfig.default}w`,
  ];

  // Build sizes attribute from responsive breakpoints
  const sizes = [
    ...variantConfig.responsive.map(
      ({ maxWidth, variant }) => `(max-width: ${maxWidth}px) ${variant}px`
    ),
    `${variantConfig.default}px`,
  ].join(', ');

  return (
    <img
      src={defaultImageUrl}
      srcSet={srcSetEntries.join(', ')}
      sizes={sizes}
      alt={alt}
      className={`animate-slide-up w-full object-contain ${className}`}
      loading="eager"
    />
  );
};

export default VendorLogo;
